import { camelCase } from 'es-toolkit'

/**
 * Normalizes a group key into the `groupXxx` form used throughout permission handling
 * (see `OuraUser.activeGroups` and overrides). Keep all callers on this helper so the
 * convention stays in one place.
 */
export function normalizeGroupKey(key: string): string {
  return camelCase('Group ' + key)
}

export class OuraUser {
  private readonly _id: string
  private readonly _email: string
  private readonly _rights: string[]
  private readonly _groups: string[]
  private readonly _audience: string
  private readonly _overrides: string[] = []
  private readonly _bypassDisabled: boolean = false

  public constructor(id: string, email: string, audience: string, groups: string[], rights: string[] = []) {
    this._id = id
    this._email = email

    this._rights = rights
    this._groups = groups
    this._audience = audience

    if (sessionStorage.OuraOverrides) {
      this._overrides = sessionStorage.OuraOverrides.split(' ')
    }

    this._bypassDisabled = sessionStorage.OuraBypassDisabled === 'true'
  }

  public get id(): string {
    return this._id
  }

  public get email(): string {
    return this._email || ''
  }

  public get audience(): string {
    return this._audience
  }

  public get groups(): readonly string[] {
    return this._groups
  }

  public get rights(): readonly string[] {
    return this._rights
  }

  public get overrides(): readonly string[] {
    return this._overrides
  }

  public get activeGroups(): readonly string[] {
    return this._groups.map((g: string) => normalizeGroupKey(g)).filter((group) => !this._overrides.includes(group))
  }

  public get activeRights(): readonly string[] {
    return this._rights.filter((right) => !this._overrides.includes(right))
  }

  public hasPermission(permission: string) {
    return (
      this.activeRights.includes(permission) ||
      this.activeGroups.includes(permission) ||
      this.activeGroups.includes(normalizeGroupKey(permission)) ||
      this.activeGroups.includes(normalizeGroupKey(permission).replace('group', 'groupBpo'))
    )
  }

  public get isOnCallUser() {
    return this.activeGroups.includes('groupCloudOnCall')
  }

  public get isBPOAgentUser() {
    return this.activeGroups.includes('groupBpoDarwinUsers')
  }

  public get hasAdminRights() {
    return this.activeGroups.includes('groupInternalToolingTeam')
  }

  public get canBypassAccessRights() {
    return this.hasAdminRights && this.audience === 'oura-tools-sandbox'
  }

  public get isBypassingAccessRights() {
    return this.canBypassAccessRights && !this._bypassDisabled
  }

  public get isWaltariAdmin() {
    return this.activeGroups.includes('groupWaltariAdmins')
  }

  public get isAppFeatureAdmin() {
    return this._overrides.includes('groupAppFeatureAdmins')
      ? false
      : this.isOnCallUser ||
          this.isWaltariAdmin ||
          this.audience.includes('sandbox') ||
          this.activeGroups.includes('groupAppFeatureAdmins')
  }

  public get isAppReleaseAdmin() {
    return this._overrides.includes('groupAppReleaseAdmins')
      ? false
      : this.isWaltariAdmin || this.audience.includes('sandbox') || this.activeGroups.includes('groupAppReleaseAdmins')
  }

  public get isOtaRolloutsAdmin() {
    return this._overrides.includes('groupFirmwareAdmins')
      ? false
      : this.isOnCallUser ||
          this.hasAdminRights ||
          this.audience.includes('sandbox') ||
          this.activeGroups.includes('groupFirmwareAdmins')
  }

  public get isOtaContentAdmin() {
    return this._overrides.includes('groupOtaContentAdmins')
      ? false
      : this.isWaltariAdmin || this.audience.includes('sandbox') || this.activeGroups.includes('groupOtaContentAdmins')
  }

  // Same groups as isContentsEditor() in the content-tooling-configs Firestore rules
  public get isContentsEditor() {
    return this._overrides.includes('groupCmsContentEditors')
      ? false
      : this.isWaltariAdmin || this.audience.includes('sandbox') || this.activeGroups.includes('groupCmsContentEditors')
  }

  public get isResearchDataAdmin() {
    return this._overrides.includes('groupResearchAdmins') ? false : this.activeGroups.includes('groupResearchAdmins')
  }
}
